import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SubscriptionBanner — full-width top-of-content bar shown when a subscription
 * requires user action (past due, cancelled, paused, or trial expiring soon).
 *
 * Renders nothing while loading, when the subscription is healthy, or after the
 * user has dismissed it for the current session.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { billingApi } from '../../lib/api';
function daysUntil(isoDate) {
    const diff = new Date(isoDate).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
}
export function SubscriptionBanner({ organisationId }) {
    const subQuery = useQuery({
        queryKey: ['subscription', organisationId ?? 'personal'],
        queryFn: () => billingApi.subscription(organisationId),
        // Refetch every 5 minutes so status changes are reflected promptly
        refetchInterval: 5 * 60 * 1000,
    });
    const sub = subQuery.data?.subscription;
    // Determine which banner variant (if any) to show, using a stable key so
    // the dismissed state resets when the subscription status changes.
    let bannerVariant = null;
    let trialDaysLeft = 0;
    if (sub) {
        if (sub.status === 'past_due') {
            bannerVariant = 'past_due';
        }
        else if (sub.status === 'cancelled') {
            bannerVariant = 'cancelled';
        }
        else if (sub.status === 'paused') {
            bannerVariant = 'paused';
        }
        else if (sub.status === 'trialing' && sub.trialEndsAt) {
            trialDaysLeft = daysUntil(sub.trialEndsAt);
            if (trialDaysLeft < 7) {
                bannerVariant = 'trial_expiring';
            }
        }
    }
    // Dismissed state resets automatically when variant changes (different key).
    const [dismissedKey, setDismissedKey] = useState(null);
    const isDismissed = dismissedKey === bannerVariant;
    // Nothing to show while loading, if subscription is healthy, or after dismiss.
    if (subQuery.isLoading || bannerVariant === null || isDismissed)
        return null;
    // ── Colour scheme ──────────────────────────────────────────────────────────
    const colours = {
        past_due: 'bg-red-50 border-b border-red-200',
        cancelled: 'bg-red-50 border-b border-red-200',
        paused: 'bg-amber-50 border-b border-amber-200',
        trial_expiring: 'bg-blue-50 border-b border-blue-200',
    };
    const textColours = {
        past_due: 'text-red-800',
        cancelled: 'text-red-800',
        paused: 'text-amber-800',
        trial_expiring: 'text-blue-800',
    };
    const dismissColours = {
        past_due: 'text-red-400 hover:text-red-600',
        cancelled: 'text-red-400 hover:text-red-600',
        paused: 'text-amber-400 hover:text-amber-600',
        trial_expiring: 'text-blue-400 hover:text-blue-600',
    };
    const buttonColours = {
        past_due: 'bg-red-700 hover:bg-red-800 text-white',
        cancelled: 'bg-red-700 hover:bg-red-800 text-white',
        paused: 'bg-amber-700 hover:bg-amber-800 text-white',
        trial_expiring: 'bg-blue-700 hover:bg-blue-800 text-white',
    };
    // ── Portal redirect helper ─────────────────────────────────────────────────
    async function handlePortalClick() {
        try {
            const { url } = await billingApi.portal({
                organisationId,
                returnUrl: window.location.href,
            });
            window.location.href = url;
        }
        catch {
            // Fail silently — the billing portal is a convenience; the Settings page
            // has a dedicated error state for this flow.
        }
    }
    // ── Content per variant ────────────────────────────────────────────────────
    function renderContent() {
        switch (bannerVariant) {
            case 'past_due':
                return (_jsxs(_Fragment, { children: [_jsx("span", { children: "Payment failed \u2014 update your payment method to restore write access." }), _jsx("button", { type: "button", onClick: handlePortalClick, className: `ml-4 shrink-0 rounded px-3 py-1 text-xs font-semibold transition-colors ${buttonColours.past_due}`, children: "Manage billing \u2192" })] }));
            case 'cancelled':
                return (_jsxs(_Fragment, { children: [_jsx("span", { children: "Your subscription has been cancelled. All data is read-only. Re-subscribe to restore write access." }), _jsx(Link, { to: "/onboarding", className: `ml-4 shrink-0 rounded px-3 py-1 text-xs font-semibold transition-colors ${buttonColours.cancelled}`, children: "View plans \u2192" })] }));
            case 'paused':
                return (_jsxs(_Fragment, { children: [_jsx("span", { children: "Your subscription is paused. Write access is suspended." }), _jsx("button", { type: "button", onClick: handlePortalClick, className: `ml-4 shrink-0 rounded px-3 py-1 text-xs font-semibold transition-colors ${buttonColours.paused}`, children: "Manage billing \u2192" })] }));
            case 'trial_expiring':
                return (_jsxs(_Fragment, { children: [_jsxs("span", { children: ["Your trial ends in ", trialDaysLeft, " ", trialDaysLeft === 1 ? 'day' : 'days', ". Add payment details now to avoid interruption."] }), _jsx("button", { type: "button", onClick: handlePortalClick, className: `ml-4 shrink-0 rounded px-3 py-1 text-xs font-semibold transition-colors ${buttonColours.trial_expiring}`, children: "Add payment details \u2192" })] }));
            default:
                return null;
        }
    }
    return (_jsx("div", { className: `${colours[bannerVariant]} px-4 py-2.5`, children: _jsxs("div", { className: "flex items-center justify-between gap-4", children: [_jsx("p", { className: `flex flex-wrap items-center text-sm ${textColours[bannerVariant]}`, children: renderContent() }), _jsx("button", { type: "button", "aria-label": "Dismiss", onClick: () => setDismissedKey(bannerVariant), className: `shrink-0 ${dismissColours[bannerVariant]}`, children: _jsx("svg", { className: "h-4 w-4", viewBox: "0 0 20 20", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" }) }) })] }) }));
}
